import { BackupChecksFilter } from "@/components/common/section-components/filters/BackupChecksFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useForm, useWatch, FieldValues } from "react-hook-form";
import {
  BackupCheckEntity,
  backupChecksFilterSchema,
} from "./schema/backup-checks.schema";
import { NoData } from "@/components/common/section-components/NoData";
import { StandardTable } from "@/components/table/StandardTable";
import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import { BackupCheckStatus } from "@/constants/entity-status";
import dayjs from "dayjs";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { BackupCheckActions } from "@/components/common/section-components/action/BackupCheckActions";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { backupChecksHeaderElements } from "./schema/header-elements";
import {
  clearFiltersHandler,
  fetchBackupChecksHandler,
  submitFiltersHandler,
} from "./schema/handler-functions";
import {
  GetBackupChecksProps,
  GetBackupChecksResponse,
} from "@/services/backup-checks.service";
import { useRoles } from "@/hooks/useRoles";
import { formatCurrency } from "@/utils/format-currency";

export const BackupChecksPage = () => {
  const [isActive, setIsActive] = useState(false);
  useEffect(() => {
    setIsActive(true);
  }, []);
  const navigate = useNavigate();
  const [loadingBackupChecks, setLoadingBackupChecks] = useState(false);

  const { fetchPermissions } = useRoles();

  const { control, handleSubmit, setValue, getValues } = useForm({
    resolver: zodResolver(backupChecksFilterSchema),
  });
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [currentFilters, setCurrentFilters] = useState<
    Partial<GetBackupChecksProps> | undefined
  >();

  const [backupChecks, setBackupChecks] =
    useState<GetBackupChecksResponse | null>({
      listado: [],
      totalPages: 0,
      totalRecords: 0,
    });

  const [page, setPage] = useState<number>(1);

  const fetchBackupChecks = async (filters?: any, showLoad = true) => {
    await fetchBackupChecksHandler({
      showLoad,
      setLoadingBackupChecks,
      setBackupChecks,
      filters,
      navigate,
      fetchPermissions,
    });
  };

  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    await submitFiltersHandler({
      event,
      fetchBackupChecks,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      navigate,
      fetchPermissions,
    });
  };

  const clearFilters = () => {
    setPage(1);
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setOpenFilters,
      setValue,
      submitFilters,
    });
  };

  const values = useWatch({
    control,
  });

  useEffect(() => {
    if (isActive) {
      fetchBackupChecks();
    }
  }, [isActive]);

  const filtersEmpty = Object.values(values ?? {}).every((v) => isEmpty(v));

  const handlePageChange = (page: number) => {
    setPage(page);
    fetchBackupChecks({ ...currentFilters, "P.Pagina": page });
  };

  const handleDetail = (id: any) => {
    navigate(AppRoutes.BACKUP_CHECKS_DETAIL.replace(":id", String(id)), {
      state: { page },
    });
  };

  const getActionElements = (item: BackupCheckEntity) => (
    <BackupCheckActions handleDetail={handleDetail} item={item} />
  );

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader title="Cheques respaldo" loading={loadingBackupChecks} />

      <BackupChecksFilter
        clearFilters={clearFilters}
        control={control}
        handleSubmit={handleSubmit}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        filtersApplied={filtersApplied}
        disableApply={filtersEmpty}
        disableFilters={loadingBackupChecks}
      />

      <Flex flexDir="column">
        {Boolean(!loadingBackupChecks && !backupChecks?.listado?.length) && (
          <NoData />
        )}
        {Boolean(!loadingBackupChecks && backupChecks?.listado?.length) && (
          <>
            <StandardTable
              actionsAlign="center"
              headerLabelAlign="center"
              actionsHeaderLabel="Ver"
              noSelection
              items={
                backupChecks?.listado?.map((el: BackupCheckEntity) => ({
                  ...el,
                  fecha: dayjs(el?.fechaCreacion).format("DD/MM/YYYY"),
                  monto: `$${formatCurrency(el?.monto ?? 0)}`,
                  displayEstado: (
                    <RequestStatusBadge
                      status={el?.estadoDescripcion as BackupCheckStatus}
                    />
                  ),
                })) ?? []
              }
              headers={backupChecksHeaderElements}
              actionElements={getActionElements}
            />
            {Number(backupChecks?.totalPages) > 1 && (
              <PaginationComponent
                page={page}
                handlePageChange={handlePageChange}
                pageSize={10}
                count={backupChecks?.totalRecords ?? 10}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
